import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Heart, Lock, Users } from 'lucide-react';

const Terms: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-gray-900 to-black text-white flex items-center justify-center font-bold text-lg shadow-lg">E</div>
            <span className="font-bold text-xl text-gray-900">Escalone</span>
          </div>
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 rounded-lg px-4 py-2 border border-gray-300 hover:border-black transition-all font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao início
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5"></div>
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 py-12 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-700 mb-6">
            <Shield className="w-4 h-4" /> Termos claros e transparentes
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 leading-tight mb-6">
            Termos de Uso & 
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Privacidade</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto mb-8">
            Entenda como protegemos seus dados e garantimos a melhor experiência na plataforma Escalone.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-green-600" />
              <span>Conforme LGPD</span>
            </div>
            <div className="flex items-center gap-2">
              <Heart className="w-4 h-4 text-red-500" />
              <span>Sem pegadinhas</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-600" />
              <span>Seus dados são seus</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <div className="prose prose-slate prose-lg max-w-none">
            <h2>1. Aceitação dos Termos</h2>
            <p>Ao acessar e utilizar a plataforma Escalone, você concorda integralmente com estes Termos de Uso e nossa Política de Privacidade. Se não concordar com qualquer disposição, não utilize nossos serviços.</p>

            <h2>2. Sobre o Escalone</h2>
            <h3>Nossa Plataforma</h3>
            <p>O Escalone é uma solução completa para gestão empresarial que oferece:</p>
            <ul>
              <li><strong>Quadros Kanban Inteligentes</strong>: Organize seus projetos com arrastar e soltar</li>
              <li><strong>CRM Integrado</strong>: Gerencie leads e relacionamentos com clientes</li>
              <li><strong>Organogramas Dinâmicos</strong>: Visualize e organize sua estrutura organizacional</li>
              <li><strong>Automações Avançadas</strong>: Configure fluxos automáticos para economizar tempo</li>
              <li><strong>Dashboard Analytics</strong>: Acompanhe métricas e performance em tempo real</li>
              <li><strong>Gestão de Documentos</strong>: Armazene e organize arquivos importantes</li>
            </ul>

            {/* Seção de planos removida */}

            <h2>3. Nossa Política de Privacidade (LGPD)</h2>
            <h3>Compromisso com sua Privacidade</h3>
            <p>Respeitamos integralmente a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018). Seus dados são tratados com máxima segurança e transparência.</p>

            <h3>Dados que Coletamos</h3>
            <p><strong>Para criar sua conta:</strong></p>
            <ul>
              <li>Nome completo e email</li>
              <li>Informações da empresa</li>
              <li>Dados de pagamento (processados por parceiros seguros)</li>
            </ul>

            <p><strong>Para melhorar sua experiência:</strong></p>
            <ul>
              <li>Como você usa a plataforma</li>
              <li>Preferências e configurações</li>
              <li>Logs de acesso para segurança</li>
            </ul>

            <h3>Seus Direitos (LGPD)</h3>
            <p>Você pode a qualquer momento:</p>
            <ul>
              <li><strong>Acessar</strong> seus dados: Saber quais informações temos</li>
              <li><strong>Corrigir</strong> dados incorretos: Atualizar informações</li>
              <li><strong>Excluir</strong> sua conta: Remover permanentemente seus dados</li>
              <li><strong>Exportar</strong> dados: Baixar suas informações</li>
              <li><strong>Contestar</strong> o uso: Questionar como usamos seus dados</li>
            </ul>
            <p><strong>Para exercer seus direitos</strong>: <a href="mailto:privacidade@escalone.com.br">privacidade@escalone.com.br</a></p>

            <h2>4. Uso Adequado da Plataforma</h2>
            <h3>O que Você Pode Fazer</h3>
            <ul>
              <li>Organizar seus processos e equipes</li>
              <li>Automatizar fluxos de trabalho</li>
              <li>Gerenciar leads e clientes</li>
              <li>Armazenar documentos da empresa</li>
              <li>Convidar colaboradores para sua organização</li>
            </ul>

            <h3>O que Não é Permitido</h3>
            <ul>
              <li>Tentar invadir ou hackear a plataforma</li>
              <li>Usar para envio de spam ou conteúdo ilegal</li>
              <li>Sobrecarregar nossos servidores intencionalmente</li>
              <li>Revender acesso à plataforma sem autorização</li>
              <li>Violar direitos autorais ou de terceiros</li>
            </ul>

            <h2>5. Cancelamento</h2>
            <h3>Cancelamento por Você</h3>
            <ul>
              <li><strong>Quando quiser</strong>: Sem período mínimo de permanência</li>
              <li><strong>Sem multa</strong>: Cancele sem custos adicionais</li>
              <li><strong>Acesso mantido</strong>: Até o fim do período já pago</li>
              <li><strong>Seus dados</strong>: Disponíveis para download por 30 dias</li>
            </ul>

            <h2>6. Contato e Suporte</h2>
            <h3>Como Falar Conosco</h3>
            <ul>
              <li><strong>Email Geral</strong>: <a href="mailto:contato@escalone.com.br">contato@escalone.com.br</a></li>
              <li><strong>Suporte Técnico</strong>: <a href="mailto:suporte@escalone.com.br">suporte@escalone.com.br</a></li>
              <li><strong>Privacidade</strong>: <a href="mailto:privacidade@escalone.com.br">privacidade@escalone.com.br</a></li>
              <li><strong>Horário</strong>: Segunda a sexta, 8h às 18h</li>
            </ul>

            <h3>Nossa Empresa</h3>
            <p><strong>Refricril Tecnologia Ltda.</strong><br />
            CNPJ: 00.000.000/0001-00<br />
            São Paulo, SP</p>
          </div>

          {/* Promises Section */}
          <div className="mt-16 p-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl border border-blue-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">✅ Nossa Promessa para Você</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-3"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Transparência Total</h4>
                  <p className="text-gray-600">Você sempre saberá como tratamos seus dados</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-600 rounded-full mt-3"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Segurança em Primeiro Lugar</h4>
                  <p className="text-gray-600">Seus dados protegidos com as melhores práticas</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-purple-600 rounded-full mt-3"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Sem Pegadinhas</h4>
                  <p className="text-gray-600">Termos claros, sem letras miúdas confusas</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-orange-600 rounded-full mt-3"></div>
                <div>
                  <h4 className="font-semibold text-gray-900">Você no Controle</h4>
                  <p className="text-gray-600">Seus dados são seus, você decide o que fazer com eles</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p><strong>Ao usar o Escalone, você confirma que leu e concordou com estes termos.</strong></p>
            <p className="mt-2">Última atualização: 20 de agosto de 2025</p>
            <p>Estes termos estão em conformidade com LGPD, Marco Civil da Internet e melhores práticas de SaaS.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-gray-900 text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white flex items-center justify-center font-bold">E</div>
            <span className="font-bold text-lg">Escalone</span>
          </div>
          <p className="text-gray-400 mb-6">A plataforma completa para transformar seus processos empresariais.</p>
          <div className="flex justify-center gap-6 text-sm">
            <button onClick={() => navigate('/')} className="hover:text-white transition-colors">Início</button>
            <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">Entrar</button>
            <button onClick={() => navigate('/cadastro')} className="hover:text-white transition-colors">Cadastrar</button>
          </div>
          <div className="mt-6 pt-6 border-t border-gray-800 text-xs text-gray-500">
            © {new Date().getFullYear()} Escalone by Refricril. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
